import React, {
	type ComponentProps,
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
} from 'react';

import { FormattedMessage } from 'react-intl';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { type Pressable } from '@atlaskit/primitives/compiled';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import { DiscoveryCTAButton } from '@atlassian/growth-pattern-library-upsell-buttons';

import { UiViewedEvent } from '../../analytics/ui-viewed-event';
import { useAnalytics } from '../../analytics/use-analytics';
import { MODAL_CONFIG } from '../../constants';
import type { EditionBenefitsRelayProps } from '../../types';
import { IntlProvider } from '../../utils/locale/intl-provider';
import { ErrorBoundary } from '../error-boundary';
import { messages } from '../messages';

import { premiumEditionBenefitsModalEntryPoint } from './premiumEditionBenefitsModal.entrypoint';

type PressableProps = Omit<ComponentProps<typeof Pressable>, 'children'>;

const PremiumBenefitModalTriggerBase = ({
	cloudId,
	product,
	children,
	variantId,
	touchpointId,
	edition,
	...pressableProps
}: Omit<EditionBenefitsRelayProps, 'projectType'> &
	PressableProps & {
		children?: ({
			ref,
			props,
		}: {
			props: PressableProps;
			ref: RefObject<HTMLElement>;
		}) => ReactNode;
	}) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { onClick: originalOnClick, ...restPressableProps } = pressableProps;

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'modalTrigger',
				actionSubjectId: 'editionBenefitsModalTrigger',
				attributes: {
					touchpointId,
					product,
					edition,
					variantId,
				},
			});
			// Call the original onClick if it exists
			originalOnClick?.(e, analyticsEvent);
		},
		[sendAnalyticsEvent, variantId, touchpointId, product, edition, originalOnClick],
	);

	const handleOnClose = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'dismiss',
			actionSubject: 'modal',
			actionSubjectId: 'editionBenefitsModal',
			attributes: {
				touchpointId,
				product,
				edition,
				variantId,
			},
		});
	}, [sendAnalyticsEvent, variantId, touchpointId, product, edition]);

	return (
		<>
			<ModalTrigger
				entryPoint={premiumEditionBenefitsModalEntryPoint}
				entryPointParams={{ cloudId, product }}
				entryPointProps={{ variantId, touchpointId, edition, onClose: handleOnClose }}
				modalProps={MODAL_CONFIG}
			>
				{({ ref }) =>
					children ? (
						children({
							ref,
							props: { ...restPressableProps, onClick: handleOnClick },
						})
					) : (
						<DiscoveryCTAButton
							ref={ref as Ref<HTMLButtonElement>}
							buttonStyle="default"
							{...restPressableProps}
							onClick={handleOnClick}
						>
							<FormattedMessage {...messages.buttonTriggerTryItFree} />
						</DiscoveryCTAButton>
					)
				}
			</ModalTrigger>
			<UiViewedEvent
				actionSubject="modalTrigger"
				actionSubjectId="editionBenefitsModalTrigger"
				attributes={{
					touchpointId,
					product,
					edition,
					variantId,
				}}
			/>
		</>
	);
};

export const PremiumBenefitModalTrigger = ({
	cloudId,
	product,
	children,
	variantId,
	touchpointId,
	edition,
	useInternalModalContextProvider,
	...pressableProps
}: EditionBenefitsRelayProps &
	PressableProps & {
		children?: ({
			ref,
			props,
		}: {
			props: PressableProps;
			ref: RefObject<HTMLElement>;
		}) => ReactNode;
	}): React.JSX.Element => {
	return (
		<ErrorBoundary variantId={variantId}>
			<IntlProvider>
				{useInternalModalContextProvider ? (
					<ModalContextProvider>
						<PremiumBenefitModalTriggerBase
							cloudId={cloudId}
							product={product}
							children={children}
							variantId={variantId}
							touchpointId={touchpointId}
							edition={edition}
							{...pressableProps}
						/>
					</ModalContextProvider>
				) : (
					<PremiumBenefitModalTriggerBase
						cloudId={cloudId}
						product={product}
						children={children}
						variantId={variantId}
						touchpointId={touchpointId}
						edition={edition}
						{...pressableProps}
					/>
				)}
			</IntlProvider>
		</ErrorBoundary>
	);
};
